import type { ReactNode } from "react";

type IconProps = {
  size?: number;
  stroke?: number;
  className?: string;
  children?: ReactNode;
  d?: string;
};

export const Icon = ({
  d,
  size = 16,
  stroke = 1.6,
  className = "",
  children,
}: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    {children ?? (d ? <path d={d} /> : null)}
  </svg>
);

type IP = Omit<IconProps, "children" | "d">;

export const Ic = {
  Map: (p: IP) => (
    <Icon {...p}>
      <path d="M9 3 3 5v16l6-2 6 2 6-2V3l-6 2-6-2Z" />
      <path d="M9 3v16" />
      <path d="M15 5v16" />
    </Icon>
  ),
  Home: (p: IP) => (
    <Icon {...p}>
      <path d="m3 10 9-7 9 7" />
      <path d="M5 9v11h14V9" />
      <path d="M10 20v-6h4v6" />
    </Icon>
  ),
  File: (p: IP) => (
    <Icon {...p}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
      <path d="M14 3v5h5" />
    </Icon>
  ),
  TrendUp: (p: IP) => (
    <Icon {...p}>
      <path d="M3 17 9 11l4 4 8-8" />
      <path d="M14 7h7v7" />
    </Icon>
  ),
  Layers: (p: IP) => (
    <Icon {...p}>
      <path d="m12 3 9 5-9 5-9-5 9-5Z" />
      <path d="m3 13 9 5 9-5" />
      <path d="m3 18 9 5 9-5" />
    </Icon>
  ),
  Ruler: (p: IP) => (
    <Icon {...p}>
      <path d="M21 6 6 21l-3-3L18 3l3 3Z" />
      <path d="m7 14 2 2" />
      <path d="m11 10 2 2" />
      <path d="m15 6 2 2" />
    </Icon>
  ),
  Search: (p: IP) => (
    <Icon {...p}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </Icon>
  ),
  Bell: (p: IP) => (
    <Icon {...p}>
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 8 3 8H3s3-1 3-8Z" />
      <path d="M10 20a2 2 0 0 0 4 0" />
    </Icon>
  ),
  User: (p: IP) => (
    <Icon {...p}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </Icon>
  ),
  Users: (p: IP) => (
    <Icon {...p}>
      <circle cx="9" cy="8" r="4" />
      <path d="M2 21a7 7 0 0 1 14 0" />
      <path d="M16 3a4 4 0 0 1 0 8" />
      <path d="M22 21a7 7 0 0 0-5-6.7" />
    </Icon>
  ),
  Plus: (p: IP) => (
    <Icon {...p}>
      <path d="M12 5v14M5 12h14" />
    </Icon>
  ),
  Arrow: (p: IP) => (
    <Icon {...p}>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </Icon>
  ),
  ArrowDown: (p: IP) => (
    <Icon {...p}>
      <path d="M12 5v14M5 13l7 7 7-7" />
    </Icon>
  ),
  Check: (p: IP) => (
    <Icon {...p}>
      <path d="m5 12 5 5 9-11" />
    </Icon>
  ),
  Cross: (p: IP) => (
    <Icon {...p}>
      <path d="M6 6l12 12M18 6 6 18" />
    </Icon>
  ),
  Dot: (p: IP) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="4" fill="currentColor" stroke="none" />
    </Icon>
  ),
  Download: (p: IP) => (
    <Icon {...p}>
      <path d="M12 4v12m0 0-5-5m5 5 5-5" />
      <path d="M5 20h14" />
    </Icon>
  ),
  Share: (p: IP) => (
    <Icon {...p}>
      <circle cx="6" cy="12" r="2.5" />
      <circle cx="18" cy="6" r="2.5" />
      <circle cx="18" cy="18" r="2.5" />
      <path d="m8 11 8-4" />
      <path d="m8 13 8 4" />
    </Icon>
  ),
  Mail: (p: IP) => (
    <Icon {...p}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </Icon>
  ),
  Pin: (p: IP) => (
    <Icon {...p}>
      <path d="M12 22s-7-7.5-7-13a7 7 0 0 1 14 0c0 5.5-7 13-7 13Z" />
      <circle cx="12" cy="9" r="2.5" />
    </Icon>
  ),
  Grid: (p: IP) => (
    <Icon {...p}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </Icon>
  ),
  Settings: (p: IP) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09A1.65 1.65 0 0 0 15 4.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.14.36.47.62.86.75H21a2 2 0 0 1 0 4h-.09c-.39.13-.72.39-.86.75Z" />
    </Icon>
  ),
  Receipt: (p: IP) => (
    <Icon {...p}>
      <path d="M5 3h14v18l-3-2-2 2-2-2-2 2-2-2-3 2Z" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </Icon>
  ),
  Palette: (p: IP) => (
    <Icon {...p}>
      <path d="M12 3a9 9 0 1 0 0 18c1.1 0 2-.9 2-2 0-.5-.2-1-.5-1.4-.3-.4-.5-.9-.5-1.4 0-1.1.9-2 2-2h1.5a4.5 4.5 0 0 0 4.5-4.5A9.5 9.5 0 0 0 12 3Z" />
      <circle cx="7.5" cy="10.5" r="1" />
      <circle cx="9.5" cy="6.5" r="1" />
      <circle cx="14.5" cy="6.5" r="1" />
      <circle cx="17" cy="10" r="1" />
    </Icon>
  ),
  Euro: (p: IP) => (
    <Icon {...p}>
      <path d="M18 7a7 7 0 1 0 0 10" />
      <path d="M4 11h10M4 14h10" />
    </Icon>
  ),
  Sparkle: (p: IP) => (
    <Icon {...p}>
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M6 18l2.5-2.5M15.5 8.5 18 6" />
    </Icon>
  ),
  Tree: (p: IP) => (
    <Icon {...p}>
      <path d="M12 3c-3 2-4 4-4 6s2 3 4 3 4-1 4-3-1-4-4-6Z" />
      <path d="M12 12v9" />
      <path d="M8 17h8" />
    </Icon>
  ),
  Droplet: (p: IP) => (
    <Icon {...p}>
      <path d="M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11Z" />
    </Icon>
  ),
  Shield: (p: IP) => (
    <Icon {...p}>
      <path d="M12 3 4 6v6c0 5 4 8 8 9 4-1 8-4 8-9V6l-8-3Z" />
    </Icon>
  ),
  Building: (p: IP) => (
    <Icon {...p}>
      <path d="M5 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16" />
      <path d="M3 21h18" />
      <path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2" />
    </Icon>
  ),
  Clock: (p: IP) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </Icon>
  ),
  External: (p: IP) => (
    <Icon {...p}>
      <path d="M14 4h6v6" />
      <path d="M20 4 10 14" />
      <path d="M20 14v6H4V4h6" />
    </Icon>
  ),
  Bolt: (p: IP) => (
    <Icon {...p}>
      <path d="M13 3 4 14h7l-1 7 9-11h-7l1-7Z" />
    </Icon>
  ),
  Star: (p: IP) => (
    <Icon {...p}>
      <path d="m12 3 3 6 7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1 3-6Z" />
    </Icon>
  ),
  Quote: (p: IP) => (
    <Icon {...p}>
      <path d="M6 17c0-3 2-5 5-5M6 11V7h4v4H6Zm8 6c0-3 2-5 5-5m-5-1V7h4v4h-4Z" />
    </Icon>
  ),
  Chevron: (p: IP) => (
    <Icon {...p}>
      <path d="m9 6 6 6-6 6" />
    </Icon>
  ),
  ChevronDown: (p: IP) => (
    <Icon {...p}>
      <path d="m6 9 6 6 6-6" />
    </Icon>
  ),
  Filter: (p: IP) => (
    <Icon {...p}>
      <path d="M3 5h18l-7 9v6l-4-2v-4L3 5Z" />
    </Icon>
  ),
  Eye: (p: IP) => (
    <Icon {...p}>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
    </Icon>
  ),
  Wallet: (p: IP) => (
    <Icon {...p}>
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <path d="M3 10h18" />
      <circle cx="16" cy="14" r="1.2" fill="currentColor" stroke="none" />
    </Icon>
  ),
  Menu: (p: IP) => (
    <Icon {...p}>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </Icon>
  ),
  Compass: (p: IP) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="m15 9-2 5-5 2 2-5 5-2Z" />
    </Icon>
  ),
  Cpu: (p: IP) => (
    <Icon {...p}>
      <rect x="6" y="6" width="12" height="12" rx="2" />
      <rect x="9" y="9" width="6" height="6" />
      <path d="M9 2v3M15 2v3M9 19v3M15 19v3M2 9h3M2 15h3M19 9h3M19 15h3" />
    </Icon>
  ),
  Database: (p: IP) => (
    <Icon {...p}>
      <ellipse cx="12" cy="6" rx="8" ry="3" />
      <path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6" />
      <path d="M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
    </Icon>
  ),
  Wand: (p: IP) => (
    <Icon {...p}>
      <path d="m3 21 13-13" />
      <path d="M15 9 21 3M18 6 15 3M21 6l-3-3" />
      <path d="M12 12h.01M9 15h.01M6 18h.01" />
    </Icon>
  ),
  PenTool: (p: IP) => (
    <Icon {...p}>
      <path d="m12 19 7-7 3 3-7 7-3-3Z" />
      <path d="M18 13 15 10l-9 9 3 3 9-9Z" />
      <path d="M2 2l7 7" />
    </Icon>
  ),
  Trash: (p: IP) => (
    <Icon {...p}>
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M10 11v6M14 11v6" />
    </Icon>
  ),
  AlertTriangle: (p: IP) => (
    <Icon {...p}>
      <path d="M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </Icon>
  ),
  Award: (p: IP) => (
    <Icon {...p}>
      <circle cx="12" cy="9" r="6" />
      <path d="m8.21 13.89-1.21 7.11 5-3 5 3-1.21-7.13" />
    </Icon>
  ),
  Volume: (p: IP) => (
    <Icon {...p}>
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    </Icon>
  ),
  Info: (p: IP) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </Icon>
  ),
  Folder: (p: IP) => (
    <Icon {...p}>
      <path d="M3 6a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6Z" />
    </Icon>
  ),
  FolderPlus: (p: IP) => (
    <Icon {...p}>
      <path d="M3 6a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6Z" />
      <path d="M12 11v6M9 14h6" />
    </Icon>
  ),
  Archive: (p: IP) => (
    <Icon {...p}>
      <rect x="3" y="4" width="18" height="4" rx="1" />
      <path d="M5 8v11a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8" />
      <path d="M10 13h4" />
    </Icon>
  ),
} as const;
